import React, { useEffect, useState } from "react";
import {
  Container,
  Row,
  Col,
  Form,
  FormGroup,
  Input,
  Button,
  Table,
  Card,
  Label,
} from "reactstrap";
import { PageSubHeader } from "components/CommonLayouts/PageSubHeader";
import { useNavigate, useParams } from "react-router-dom";
import { get, post, put } from "helpers/api_helper";
import * as api from "../../constants/api_constants";
import Footer from "components/VerticalLayout/Footer";
import { showSuccessToast } from "helpers/toastMessages.js";
import { getAllCountries } from "helpers/utils/locationUtil.js";
import * as Yup from "yup";
import { useFormik } from "formik";
import DragAndDropComponent from "components/FileUploadDrag/DragAndDrop.js";
import { uri } from "helpers/api_helper";

const TAX_RATES = {
  "CGST(9%)": 9,
  "SGST(9%)": 9,
  "IGST(18%)": 18,
  "VAT(15%)": 15,
  "SERVICE(10%)": 10,
};

const TAX_OPTIONS = Object.keys(TAX_RATES).map((key) => ({
  value: key,
  label: key,
}));

const NewInvoice = () => {
  const navigate = useNavigate();
  const { invoiceId } = useParams();

  const validationSchema = Yup.object().shape({
    invoiceDetails: Yup.object().shape({
      invoiceDate: Yup.date().required("Invoice date is required"),
      dueDate: Yup.date()
        .required("Due date is required")
        .min(Yup.ref("invoiceDate"), "Due date must be after the invoice date"),
    }),
  });

  const [state, setState] = useState({
    imagePreview: null,
    logoFile: {},
    countries: [],
    customers: [],
    employees: [],
    projects: [],
    assignments: [],
    invoiceItems: [
      {
        uid: "",
        type: "service",
        employeeId: "",
        projectId: "",
        assignmentId: "",
        invoiceMonth: "",
        description: "",
        unitPrice: "",
        quantity: "",
        total: "",
        tax1Type: "",
        tax1Amount: "",
        tax2Type: "",
        tax2Amount: "",
      },
    ],
    isTaxRequired: false,
    isPoNumberRequired: false,
    amountPaid: 0.0,
    poNumber: "",
    status: "DRAFT",
    totals: {
      subTotal: 0,
      tax1Total: 0,
      tax2Total: 0,
      grandTotal: 0,
      balanceDue: 0,
    },
    selectedEmployeeData: {
      month: "",
      employeeId: null,
      employeeName: "",
      projectId: null,
      projectName: "",
      assignmentId: null,
      assignmentName: "",
      units: "",
      payrate: "",
    },
    toSelectedCustomer: {
      uid: "",
      toCustomerId: "",
      toCustomerName: "",
      toCustomerContact: "",
      toCustomerAddress: "",
      toCustomerCurrency: "",
    },
    invoiceDetails: {
      invoiceNumber: "",
      invoiceDate: new Date().toISOString().split("T")[0],
      dueDate: "",
    },
    fromCustomer: {
      fromCustomerName: "",
      fromCustomerAddress: "",
      fromCustomerContact: "",
      uid: "",
    },
    notes: "",
  });

  const formik = useFormik({
    initialValues: {
      invoiceDetails: {
        invoiceNumber: state.invoiceDetails.invoiceNumber,
        invoiceDate: state.invoiceDetails.invoiceDate,
        dueDate: state.invoiceDetails.dueDate,
      },
    },
    validationSchema,
    enableReinitialize: true,
    onSubmit: (values) => {
      updateState("invoiceDetails", values.invoiceDetails);
      handleSave();
    },
  });

  const updateState = (key, value) => {
    setState((prevState) => ({
      ...prevState,
      [key]: value,
    }));
  };

  const updateNestedState = (parentKey, childKey, value) => {
    setState((prevState) => ({
      ...prevState,
      [parentKey]: {
        ...prevState[parentKey],
        [childKey]: value,
      },
    }));
    formik.setFieldValue(`${parentKey}.${childKey}`, value);
  };

  useEffect(() => {
    fetchCustomerData();
    fetchEmployeesData();
    const countriesData = getAllCountries();
    updateState("countries", countriesData);
    if (invoiceId) {
      fetchInvoiceData();
    }
  }, []);

  useEffect(() => {
    calculateTotals();
  }, [state.invoiceItems, state.isTaxRequired]);

  useEffect(() => {
    if (
      state.selectedEmployeeData.month &&
      state.selectedEmployeeData.employeeId
    ) {
      fetchEmployeeAssignments();
    }
  }, [
    state.selectedEmployeeData.month,
    state.selectedEmployeeData.employeeId,
    state.selectedEmployeeData.projectId,
  ]);

  useEffect(() => {
    if (state.selectedEmployeeData.employeeId) {
      fetchEmployeeUnitsPayrate();
    }
  }, [
    state.selectedEmployeeData.month,
    state.selectedEmployeeData.employeeId,
    state.selectedEmployeeData.assignmentId,
  ]);

  const fetchInvoiceData = async () => {
    try {
      const response = await get(api.GET_INVOICE_BY_ID(invoiceId));
      updateState("fromCustomer", {
        fromCustomerName: response?.invoiceFromCustomer?.companyName ?? "",
        fromCustomerAddress: response?.invoiceFromCustomer?.address ?? "",
        fromCustomerContact:
          response?.invoiceFromCustomer?.customerContact ?? "",
        uid: response?.invoiceFromCustomer?.id ?? "",
      });

      updateState("toSelectedCustomer", {
        uid: response?.invoiceToCustomer?.id ?? "",
        toCustomerId: response?.toCustomerId ?? "",
        toCustomerName: response?.invoiceToCustomer?.companyName ?? "",
        toCustomerContact: response?.invoiceToCustomer?.customerContact ?? "",
        toCustomerAddress: response?.invoiceToCustomer?.address ?? "",
        toCustomerCurrency: response?.currency ?? "",
      });

      updateState("invoiceDetails", {
        invoiceNumber: response?.invoiceCode ?? "",
        invoiceDate: response?.invoiceDate ?? "",
        dueDate: response?.dueDate ?? "",
      });

      const lineItems = response?.lineItems ?? [];
      const updatedInvoiceItems = lineItems.map((item) => ({
        uid: item?.id ?? "",
        type: item?.itemType ?? "",
        employeeId: item?.employeeId ?? null,
        projectId: item?.projectId ?? null,
        assignmentId: item?.assignmentId ?? null,
        invoiceMonth: item?.invoiceMonth ? item.invoiceMonth.slice(0, 7) : "",
        description: item?.description ?? "",
        unitPrice: item?.unitPrice ?? "",
        quantity: item?.quantity ?? "",
        total: item?.subtotal ?? "",
        tax1Type: item?.taxType1 ?? "",
        tax1Amount: item?.taxAmount1 ?? 0,
        tax2Type: item?.taxType2 ?? "",
        tax2Amount: item?.taxAmount2 ?? 0,
      }));

      if (updatedInvoiceItems[0]?.tax1Type !== "") {
        updateState("isTaxRequired", true);
      }
      updateState("invoiceItems", updatedInvoiceItems);
      updateState("poNumber", response?.poNumber ?? "");
      if (response?.poNumber) {
        updateState("isPoNumberRequired", true);
      }

      updateState("notes", response?.notes ?? "");
      updateState("amountPaid", response?.advanceAmount);

      if (response?.logoId) {
        try {
          const logoResponse = await get(
            `${uri}/fileservice/api/files/view/${response.logoId}`
          );
          if (logoResponse.fileContent) {
            const fileTypeList = logoResponse.fileName.split(".");
            const fileType =
              fileTypeList[fileTypeList.length - 1].toLowerCase();
            const mimeType = ["jpg", "jpeg"].includes(fileType)
              ? "image/jpeg"
              : fileType === "png"
              ? "image/png"
              : fileType === "gif"
              ? "image/gif"
              : "application/octet-stream";
            const base64 = `data:${mimeType};base64,${logoResponse.fileContent}`;
            updateState("imagePreview", base64);
            updateState("logoFile", {
              id: response.logoId,
              fileName: logoResponse.fileName,
              name: logoResponse.fileName,
              path:
                logoResponse.filePath || `/Uploads/${logoResponse.fileName}`,
            });
          }
        } catch (error) {
          console.error("Error fetching logo file:", error);
        }
      }
    } catch (error) {
      console.error("Error fetching invoice data:", error);
    }
  };

  const fetchCustomerData = async () => {
    try {
      const customersData = await get(
        api.GET_ALL_CUSTOMERS_ID_NAME_MAIL_CURRENCY
      );
      const updatedCustData = customersData.map((item) => ({
        value: item.customerId,
        label: `${item.companyName} (${item.customerCode})`,
        companyName: item.companyName,
        email: item.email,
        country: item.country,
        currency: item.currency,
        province: item.province,
        city: item.city,
        address: item.street,
        zipCode: item.zipCode,
      }));
      updateState("customers", updatedCustData);
    } catch (error) {
      console.error("Error fetching customers data:", error);
    }
  };

  const fetchEmployeesData = async () => {
    try {
      const employeesData = await get(api.GET_ALL_EMPLOYEES_NAME_ID_MAIL);
      const updatedEmployeeData = employeesData.map((item) => ({
        id: item.employeeId,
        value: item.employeeId,
        name: `${item.firstName} ${item.lastName} - (${item.employeeCode})`,
        employeeName: `${item.firstName} ${item.lastName}`,
      }));
      updateState("employees", updatedEmployeeData);
    } catch (error) {
      console.error("Error fetching employees data:", error);
    }
  };

  const fetchEmployeeAssignments = async () => {
    try {
      const response = await post(api.GET_PROJ_ASSIGN_BY_EMPID, {
        employeeId: state.selectedEmployeeData.employeeId,
        subscriptionId: 100,
        date: state.selectedEmployeeData.month,
      });
      updateState("projects", response.projects || []);
      if (state.selectedEmployeeData.projectId) {
        const assignValues = response.projectAssignmentsMap;
        updateState(
          "assignments",
          assignValues[state.selectedEmployeeData.projectId] || []
        );
      }
    } catch (error) {
      console.error("Error fetching assignments data:", error);
    }
  };

  const fetchEmployeeUnitsPayrate = async () => {
    try {
      const response = await post(api.GET_TIMESHEET_UNITS_PAYRATE, {
        employeeId: state.selectedEmployeeData.employeeId,
        subscriptionId: 100,
        date: state.selectedEmployeeData.month,
        assignmentId: state.selectedEmployeeData.assignmentId,
      });

      updateNestedState(
        "selectedEmployeeData",
        "invoiceCode",
        response.invoiceCode
      );
      updateNestedState("selectedEmployeeData", "units", response.hoursWorked);
      updateNestedState("selectedEmployeeData", "payrate", response.rate);
      updateNestedState(
        "invoiceDetails",
        "invoiceNumber",
        response.invoiceCode
      );

      const initialUnitPrice = response.rate;
      const initialQuantity = response.hoursWorked;
      const initialTotal = (initialUnitPrice * initialQuantity).toFixed(2);

      const newInvoiceItems = state.invoiceItems?.map((each, index) => {
        if (index === state.invoiceItems.length - 1) {
          const updatedItem = {
            ...each,
            employeeId: state.selectedEmployeeData.employeeId,
            projectId: state.selectedEmployeeData.projectId,
            assignmentId: state.selectedEmployeeData.assignmentId,
            invoiceMonth: state.selectedEmployeeData.month,
            description: `Employee: ${state.selectedEmployeeData.employeeName}\nProject: ${state.selectedEmployeeData.projectName}\nAssignment: ${state.selectedEmployeeData.assignmentName}`,
            unitPrice: initialUnitPrice,
            quantity: initialQuantity,
            total: initialTotal,
          };

          if (state.isTaxRequired) {
            if (updatedItem.tax1Type) {
              const tax1Rate = TAX_RATES[updatedItem.tax1Type] || 0;
              updatedItem.tax1Amount = (
                (Number(initialTotal) * tax1Rate) /
                100
              ).toFixed(2);
            }
            if (updatedItem.tax2Type) {
              const tax2Rate = TAX_RATES[updatedItem.tax2Type] || 0;
              updatedItem.tax2Amount = (
                (Number(initialTotal) * tax2Rate) /
                100
              ).toFixed(2);
            }
          }

          return updatedItem;
        }
        return each;
      });

      updateState("invoiceItems", newInvoiceItems);
    } catch (error) {
      console.error("Error fetching assignments data:", error);
    }
  };

  const addNewItem = () => {
    setState((prevState) => ({
      ...prevState,
      invoiceItems: [
        ...prevState.invoiceItems,
        {
          type: "service",
          employeeId: "",
          projectId: "",
          assignmentId: "",
          invoiceMonth: "",
          description: "",
          unitPrice: "",
          quantity: "",
          total: "",
          tax1Type: "",
          tax1Amount: "",
          tax2Type: "",
          tax2Amount: "",
        },
      ],
    }));
    updateState("selectedEmployeeData", {
      month: "",
      employeeId: "",
      projectId: "",
      assignmentId: "",
      units: "",
      payrate: "",
    });
  };

  const removeItem = (index) => {
    const updatedItems = state.invoiceItems.filter((_, i) => i !== index);
    setState((prevState) => ({
      ...prevState,
      invoiceItems: updatedItems,
    }));
  };

  const calculateTotals = (amountNew = 0) => {
    const { invoiceItems, isTaxRequired } = state;
    const calculation = invoiceItems.reduce(
      (acc, item) => {
        const itemTotal = Number(item.total) || 0;
        const tax1Amount = isTaxRequired
          ? Number(item.tax1Amount) ||
            (item.tax1Type
              ? (itemTotal * (TAX_RATES[item.tax1Type] || 0)) / 100
              : 0)
          : 0;
        const tax2Amount = isTaxRequired
          ? Number(item.tax2Amount) ||
            (item.tax2Type
              ? (itemTotal * (TAX_RATES[item.tax2Type] || 0)) / 100
              : 0)
          : 0;

        return {
          subTotal: acc.subTotal + itemTotal,
          tax1Total: acc.tax1Total + tax1Amount,
          tax2Total: acc.tax2Total + tax2Amount,
        };
      },
      { subTotal: 0, tax1Total: 0, tax2Total: 0 }
    );

    const grandTotal =
      calculation.subTotal + calculation.tax1Total + calculation.tax2Total;
    const balanceDue = grandTotal - amountNew;

    updateState("totals", {
      ...calculation,
      grandTotal,
      balanceDue,
    });
  };

  const handleInputChange = (index, field, value) => {
    const updatedItems = [...state.invoiceItems];
    updatedItems[index][field] = value;

    if (field === "unitPrice" || field === "quantity") {
      const unitPrice = Number(updatedItems[index].unitPrice) || 0;
      const quantity = Number(updatedItems[index].quantity) || 0;
      const total = unitPrice * quantity;

      updatedItems[index].total = total.toFixed(2);

      if (state.isTaxRequired) {
        if (updatedItems[index].tax1Type) {
          const tax1Rate = TAX_RATES[updatedItems[index].tax1Type] || 0;
          updatedItems[index].tax1Amount = ((total * tax1Rate) / 100).toFixed(
            2
          );
        }
        if (updatedItems[index].tax2Type) {
          const tax2Rate = TAX_RATES[updatedItems[index].tax2Type] || 0;
          updatedItems[index].tax2Amount = ((total * tax2Rate) / 100).toFixed(
            2
          );
        }
      }
    }

    if (field === "tax1Type") {
      const total = Number(updatedItems[index].total) || 0;
      const taxRate = TAX_RATES[value] || 0;
      updatedItems[index].tax1Amount = ((total * taxRate) / 100).toFixed(2);
      // If tax2Type is the same as the newly selected tax1Type, reset tax2Type
      if (updatedItems[index].tax2Type === value) {
        updatedItems[index].tax2Type = "";
        updatedItems[index].tax2Amount = "";
      }
    }

    if (field === "tax2Type") {
      const total = Number(updatedItems[index].total) || 0;
      const taxRate = TAX_RATES[value] || 0;
      updatedItems[index].tax2Amount = ((total * taxRate) / 100).toFixed(2);
    }

    updateState("invoiceItems", updatedItems);
  };

  const handleTaxRequiredChange = () => {
    updateState("isTaxRequired", !state.isTaxRequired);
  };

  const handlePoNumberRequiredChange = () => {
    updateState("isPoNumberRequired", !state.isPoNumberRequired);
  };

  const handleDateChange = (e) => {
    const monthYear = e.target.value;
    updateNestedState("selectedEmployeeData", "month", monthYear);
  };

  const handleEmployeeChange = (e) => {
    const { value } = e.target;
    updateNestedState("selectedEmployeeData", "employeeId", value);
    const empName = state.employees.filter(
      (employee) => employee.id === parseInt(value)
    );
    if (empName.length > 0) {
      updateNestedState(
        "selectedEmployeeData",
        "employeeName",
        empName[0].employeeName
      );
    }
  };

  const handleProjectChange = (e) => {
    const { value } = e.target;
    updateNestedState("selectedEmployeeData", "projectId", value);
    const selectedProjectDetails = state.projects.filter(
      (p) => p.projectId === parseInt(value)
    );
    if (selectedProjectDetails.length > 0) {
      updateNestedState(
        "selectedEmployeeData",
        "projectName",
        selectedProjectDetails[0].projectName
      );
    }
  };

  const handleAssignmentChange = (e) => {
    const { value } = e.target;
    updateNestedState("selectedEmployeeData", "assignmentId", value);
    const selectedAssignmentDetails = state.assignments.filter(
      (a) => a.assignmentId === parseInt(value)
    );
    if (selectedAssignmentDetails.length > 0) {
      updateNestedState(
        "selectedEmployeeData",
        "assignmentName",
        selectedAssignmentDetails[0].assignmentTitle
      );
    }
  };

  const handleCustomerChange = (e) => {
    const { value } = e.target;
    updateNestedState("toSelectedCustomer", "toCustomerId", value);
    const selectedCustomerDetails = state.customers.filter(
      (c) => c.value === parseInt(value)
    );
    if (selectedCustomerDetails.length > 0) {
      updateNestedState(
        "toSelectedCustomer",
        "toCustomerName",
        selectedCustomerDetails[0].companyName
      );
      updateNestedState(
        "toSelectedCustomer",
        "toCustomerContact",
        selectedCustomerDetails[0].email
      );
      updateNestedState(
        "toSelectedCustomer",
        "toCustomerAddress",
        `${selectedCustomerDetails[0].address}, ${selectedCustomerDetails[0].city}, ${selectedCustomerDetails[0].province}, ${selectedCustomerDetails[0].country}, ${selectedCustomerDetails[0].zipCode}`
      );
      updateNestedState(
        "toSelectedCustomer",
        "toCustomerCurrency",
        selectedCustomerDetails[0].currency
      );
    }
  };

  const handleCurrencyChange = (e) => {
    const { value } = e.target;
    updateNestedState("toSelectedCustomer", "toCustomerCurrency", value);
  };

  const handleAmountPaidChange = (e) => {
    const newAmount = e.target.value;
    if (!isNaN(newAmount) && newAmount >= 0) {
      updateState("amountPaid", newAmount);
      calculateTotals(newAmount);
    }
  };

  const handleFileUpload = async (fileWithId) => {
    if (fileWithId.id) {
      try {
        const response = await get(
          `${uri}/fileservice/api/files/view/${fileWithId.id}`
        );
        if (response.fileContent) {
          const fileTypeList = response.fileName.split(".");
          const fileType = fileTypeList[fileTypeList.length - 1].toLowerCase();
          const mimeType = ["jpg", "jpeg"].includes(fileType)
            ? "image/jpeg"
            : fileType === "png"
            ? "image/png"
            : fileType === "gif"
            ? "image/gif"
            : "application/octet-stream";
          const base64 = `data:${mimeType};base64,${response.fileContent}`;
          updateState("imagePreview", base64);
          updateState("logoFile", fileWithId);
        }
      } catch (error) {
        console.error("Error fetching uploaded file:", error);
      }
    } else {
      updateState("imagePreview", null);
      updateState("logoFile", {});
    }
  };

  const handleCustomerNameChange = (e) => {
    const { value } = e.target;
    updateNestedState("toSelectedCustomer", "toCustomerName", value);
  };

  const handleCustomerContactChange = (e) => {
    const { value } = e.target;
    updateNestedState("toSelectedCustomer", "toCustomerContact", value);
  };

  const handleCustomerAddressChange = (e) => {
    const { value } = e.target;
    updateNestedState("toSelectedCustomer", "toCustomerAddress", value);
  };

  const handleInvoiceNumberChange = (e) => {
    const { value } = e.target;
    updateNestedState("invoiceDetails", "invoiceNumber", value);
  };

  const handleInvoiceDateChange = (e) => {
    const { value } = e.target;
    updateNestedState("invoiceDetails", "invoiceDate", value);
  };

  const handleDueDateChange = (e) => {
    const { value } = e.target;
    updateNestedState("invoiceDetails", "dueDate", value);
  };

  const transformInvoiceItems = (invoiceItems) => {
    return invoiceItems.map((item) => ({
      id: item.uid || "",
      itemType: item.type,
      employeeId: item.employeeId,
      projectId: item.projectId,
      assignmentId: item.assignmentId,
      invoiceMonth: item.invoiceMonth,
      description: item.description,
      quantity: Number(item.quantity) || 0,
      unitPrice: Number(item.unitPrice) || 0,
      taxType1: item.tax1Type,
      taxAmount1: Number(item.tax1Amount) || 0,
      taxType2: item.tax2Type,
      taxAmount2: Number(item.tax2Amount) || 0,
      subtotal: Number(item.total) || 0,
    }));
  };

  const handleSave = async () => {
    const errors = await formik.validateForm();
    if (Object.keys(errors).length > 0) {
      formik.setTouched({
        invoiceDetails: { invoiceDate: true, dueDate: true },
      });
      return;
    }

    const payload = {
      invoiceDTO: {
        subscriptionId: 100,
        toCustomerId: state.toSelectedCustomer.toCustomerId,
        invoiceCode: state.invoiceDetails.invoiceNumber,
        employeeId:
          state.selectedEmployeeData.employeeId != null
            ? String(state.selectedEmployeeData.employeeId)
            : "",
        projectId:
          state.selectedEmployeeData.projectId != null
            ? String(state.selectedEmployeeData.projectId)
            : "",
        assignmentId:
          state.selectedEmployeeData.assignmentId != null
            ? String(state.selectedEmployeeData.assignmentId)
            : "",

        total: state.totals.grandTotal,
        invoiceDate: state.invoiceDetails.invoiceDate,
        dueDate: state.invoiceDetails.dueDate,
        advanceAmount: state.amountPaid,
        currency: state.toSelectedCustomer.toCustomerCurrency,
        status: state.status,
        // createdAt: state.invoiceDetails.invoiceDate,
        poNumber: state.poNumber,
        isRecurring: true,
        // invoiceRevisedCode: "",
        invoiceCustomerDTO: {
          name: state.toSelectedCustomer.toCustomerName,
          address: state.toSelectedCustomer.toCustomerAddress,
        },
        lineItems: transformInvoiceItems(state.invoiceItems),
        invoiceFromCustomer: {
          id: state.fromCustomer.uid,
          address: state.fromCustomer.fromCustomerAddress,
          companyName: state.fromCustomer.fromCustomerName,
          customerContact: state.fromCustomer.fromCustomerContact,
        },
        invoiceToCustomer: {
          address: state.toSelectedCustomer.toCustomerAddress,
          companyName: state.toSelectedCustomer.toCustomerName,
          customerContact: state.toSelectedCustomer.toCustomerContact,
          id: state.toSelectedCustomer.uid,
        },
        notes: state.notes,
        logo: state.logoFile?.id ? String(state.logoFile.id) : null,
      },
    };

    try {
      if (invoiceId) {
        const invoiceResponse = await put(
          api.UPDATE_INVOICE(invoiceId),
          payload
        );
        if (invoiceResponse) {
          showSuccessToast("Invoice updated successfully");
          navigate("/invoice", {
            state: {
              invoiceData: { ...payload.invoiceDTO, logo: state.imagePreview },
            },
          });
        }
      } else {
        const invoiceResponse = await post(api.CREATE_INVOICE, payload);
        if (invoiceResponse) {
          showSuccessToast("Invoice created successfully");
          navigate("/invoice", {
            state: {
              invoiceData: { ...payload.invoiceDTO, logo: state.imagePreview },
            },
          });
        }
      }
    } catch (error) {
      console.error("Error saving invoice:", error);
    }
  };

  return (
    <div className="page-content">
      <Container fluid className="p-0">
        <PageSubHeader
          heading={"New Invoice"}
          routePath={"Home"}
          pageName={"Invoices"}
        />

        <Card className="p-4">
          <Form onSubmit={formik.handleSubmit}>
            <Row className="d-flex justify-content-between align-items-center row-gap-2">
              <Col
                md="6"
                className="d-flex align-items-center gap-3"
                style={{ height: "px" }}
              >
                <h6 className="text-primary text-nowrap text-bold">
                  Custom Settings:
                </h6>
                <Input
                  name="toCustomerCurrency"
                  type="select"
                  className="form-control"
                  id="toCustomerCurrency"
                  onChange={handleCurrencyChange}
                >
                  <option value="">Select Currency</option>
                  {state.countries.map((item, index) => (
                    <option key={index} value={item.currency}>
                      {item.name} - {item.currency}
                    </option>
                  ))}
                </Input>
                <FormGroup check className="d-flex align-items-center gap-2">
                  <Input
                    type="checkbox"
                    id="taxRequiredCheckbox"
                    checked={state.isTaxRequired}
                    onChange={handleTaxRequiredChange}
                    style={{ cursor: "pointer" }}
                  />
                  <Label className="mb-0" for="taxRequiredCheckbox" check>
                    Tax
                  </Label>
                </FormGroup>

                <FormGroup check className="d-flex align-items-center gap-2">
                  <Input
                    type="checkbox"
                    id="poNumberRequiredCheckbox"
                    checked={state.isPoNumberRequired}
                    onChange={handlePoNumberRequiredChange}
                    style={{ cursor: "pointer" }}
                  />
                  <Label
                    className="mb-0 text-nowrap"
                    for="poNumberRequiredCheckbox"
                    check
                  >
                    PO Number
                  </Label>
                </FormGroup>
              </Col>

              <Col md="3" className="d-flex align-items-center gap-2 ml-auto">
                <Label className="text-nowrap text-primary mt-2">
                  Invoice Status:
                </Label>
                <Input
                  name="status"
                  type="select"
                  className="form-control"
                  id="status"
                  onChange={(e) => {
                    updateState("status", e.target.value);
                  }}
                >
                  <option value="DRAFT">Draft</option>
                  <option value="APPROVED">Approved</option>
                  <option value="REJECTED">Rejected</option>
                  <option value="SEND">Send</option>
                  <option value="SENT">Sent</option>
                  <option value="PAID">Paid</option>
                  <option value="OVERDUE">Overdue</option>
                  <option value="RESUBMITTED">Re-Submitted</option>
                  <option value="PARTIALLY_PAID">Partially Paid</option>
                </Input>
              </Col>
            </Row>

            <Row className="text-center mt-2">
              <h1>INVOICE</h1>
            </Row>

            <Row className="mb-5 d-flex justify-content-between">
              <Col md={6}>
                <div className="py-3">
                  <h5 className="text-primary">From Customer</h5>
                  <FormGroup>
                    <Input
                      type="text"
                      placeholder="From Company Name"
                      className="mb-3"
                      value={state.fromCustomer.fromCustomerName}
                      onChange={(e) =>
                        updateNestedState(
                          "fromCustomer",
                          "fromCustomerName",
                          e.target.value
                        )
                      }
                    />
                  </FormGroup>
                  <FormGroup>
                    <Input
                      type="textarea"
                      rows={3}
                      placeholder="Enter Address.."
                      value={state.fromCustomer.fromCustomerAddress}
                      onChange={(e) =>
                        updateNestedState(
                          "fromCustomer",
                          "fromCustomerAddress",
                          e.target.value
                        )
                      }
                    />
                  </FormGroup>
                </div>
              </Col>

              <Col
                md={3}
                className="d-flex flex-column align-items-end justify-content-end"
              >
                <h5 className="text-black">Upload logo</h5>
                <div className="d-flex">
                  <div className="w-[80px] h-[120px]">
                    <DragAndDropComponent
                      initialFile={state.logoFile}
                      handleFileUpload={handleFileUpload}
                      readOnly={false}
                    />
                    {state.imagePreview && (
                      <div className="mt-4">
                        <img
                          src={state.imagePreview}
                          alt="Uploaded Logo"
                          style={{
                            maxWidth: "150px",
                            maxHeight: "150px",
                            objectFit: "cover",
                          }}
                        />
                      </div>
                    )}
                  </div>
                </div>
              </Col>
            </Row>

            <Row className="justify-content-between mb-4">
              <Col md={6}>
                <h5 className="text-primary">To Customer</h5>
                <FormGroup>
                  <Input
                    type="select"
                    name="customer"
                    id="customer"
                    className="form-control"
                    value={state.toSelectedCustomer.toCustomerId}
                    onChange={handleCustomerChange}
                  >
                    <option value="">Select Customer</option>
                    {state.customers.map((item) => (
                      <option key={item.value} value={item.value}>
                        {item.label}
                      </option>
                    ))}
                  </Input>
                </FormGroup>
                <FormGroup>
                  <Input
                    type="text"
                    placeholder="Customer Name"
                    className="mb-3"
                    value={state.toSelectedCustomer.toCustomerName}
                    onChange={handleCustomerNameChange}
                  />
                </FormGroup>

                <FormGroup>
                  <Input
                    type="text"
                    placeholder="Customer Contact"
                    className="mb-3"
                    value={state.toSelectedCustomer.toCustomerContact}
                    onChange={handleCustomerContactChange}
                  />
                </FormGroup>

                <FormGroup>
                  <Input
                    type="textarea"
                    placeholder="Customer Address"
                    rows="3"
                    className="mb-3"
                    value={state.toSelectedCustomer.toCustomerAddress}
                    onChange={handleCustomerAddressChange}
                  />
                </FormGroup>
              </Col>
              <Col md={4}>
                <FormGroup>
                  <Label>Invoice #</Label>
                  <Input
                    type="text"
                    className="mb-3"
                    value={state.invoiceDetails.invoiceNumber}
                    onChange={handleInvoiceNumberChange}
                  />
                </FormGroup>

                <FormGroup>
                  <Label>Invoice Date</Label>
                  <Input
                    type="date"
                    className="mb-3"
                    value={state.invoiceDetails.invoiceDate}
                    onChange={handleInvoiceDateChange}
                    onBlur={formik.handleBlur}
                  />
                  {formik.touched.invoiceDetails?.invoiceDate &&
                    formik.errors.invoiceDetails?.invoiceDate && (
                      <div className="text-danger">
                        {formik.errors.invoiceDetails.invoiceDate}
                      </div>
                    )}
                </FormGroup>

                <FormGroup>
                  <Label>Due Date<span className="text-danger">*</span></Label>
                  <Input
                    type="date"
                    className="mb-3"
                    value={state.invoiceDetails.dueDate}
                    onChange={handleDueDateChange}
                    onBlur={formik.handleBlur}
                  />
                  {formik.touched.invoiceDetails?.dueDate &&
                    formik.errors.invoiceDetails?.dueDate && (
                      <div className="text-danger">
                        {formik.errors.invoiceDetails.dueDate}
                      </div>
                    )}
                </FormGroup>

                {state.isPoNumberRequired && (
                  <FormGroup>
                    <Label>PO Number</Label>
                    <Input
                      type="text"
                      placeholder="Enter PO Number"
                      className="mb-3"
                      value={state.poNumber}
                      onChange={(e) => updateState("poNumber", e.target.value)}
                    />
                  </FormGroup>
                )}
              </Col>
            </Row>

            <div className="mb-4">
              <Row>
                <h5 className="text-primary">Please Select</h5>
              </Row>
              <Row className="mb-3 justify-content-start">
                <Col md={3}>
                  <FormGroup>
                    <Input
                      type="month"
                      id="monthInput"
                      placeholder="Select month and year"
                      value={state.selectedEmployeeData.month}
                      onChange={handleDateChange}
                    />
                  </FormGroup>
                </Col>
                <Col md={3}>
                  <FormGroup>
                    <Input type="select" onChange={handleEmployeeChange}>
                      <option value="">Select Employee</option>
                      {state.employees.map((employee) => (
                        <option key={employee.id} value={employee.id}>
                          {employee.name}
                        </option>
                      ))}
                    </Input>
                  </FormGroup>
                </Col>
                {state.selectedEmployeeData.month &&
                  state.selectedEmployeeData.employeeId && (
                    <>
                      <Col md={3}>
                        <FormGroup>
                          <Input type="select" onChange={handleProjectChange}>
                            <option value="">Select Project</option>
                            {state.projects.map((proj) => (
                              <option
                                key={proj.projectId}
                                value={proj.projectId}
                              >
                                {proj.projectName} - {proj.projectCode}
                              </option>
                            ))}
                          </Input>
                        </FormGroup>
                      </Col>
                      <Col md={3}>
                        <FormGroup>
                          <Input
                            type="select"
                            onChange={handleAssignmentChange}
                          >
                            <option value="">Select Assignment</option>
                            {state.assignments.map((assign) => (
                              <option
                                key={assign.assignmentId}
                                value={assign.assignmentId}
                              >
                                {assign.assignmentTitle} -{" "}
                                {assign.assignmentCode}
                              </option>
                            ))}
                          </Input>
                        </FormGroup>
                      </Col>
                    </>
                  )}
              </Row>

              <div className="mb-4">
                <Table responsive className="mb-0">
                  <thead>
                    <tr>
                      <th style={{ width: "12%" }}>Item</th>
                      <th style={{ width: "20%" }}>Description</th>
                      <th style={{ width: "12%" }}>Unit Price</th>
                      <th style={{ width: "10%" }}>Quantity</th>
                      {state.isTaxRequired && (
                        <>
                          <th style={{ width: "12%" }}>Tax 1</th>
                          <th style={{ width: "12%" }}>Tax 2</th>
                        </>
                      )}
                      <th style={{ width: "12%" }}>Total</th>
                      <th style={{ width: "4%" }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {state.invoiceItems.map((item, index) => {
                      const availableTax2Options = TAX_OPTIONS.filter(
                        (option) => option.value !== item.tax1Type
                      );
                      return (
                        <tr key={index}>
                          <td>
                            <Input
                              type="select"
                              value={item.type}
                              onChange={(e) =>
                                handleInputChange(index, "type", e.target.value)
                              }
                            >
                              <option value="service">Service</option>
                              <option value="product">Product</option>
                              <option value="hours">Hours</option>
                              <option value="days">Days</option>
                            </Input>
                          </td>
                          <td>
                            <Input
                              type="textarea"
                              value={item.description}
                              placeholder="Description"
                              rows={
                                state.selectedEmployeeData.employeeId &&
                                state.selectedEmployeeData.projectId &&
                                state.selectedEmployeeData.assignmentId
                                  ? 3
                                  : 1
                              }
                              onChange={(e) =>
                                handleInputChange(
                                  index,
                                  "description",
                                  e.target.value
                                )
                              }
                            />
                          </td>
                          <td>
                            <Input
                              type="text"
                              value={item.unitPrice}
                              placeholder="Unit Price"
                              onChange={(e) =>
                                handleInputChange(
                                  index,
                                  "unitPrice",
                                  e.target.value
                                )
                              }
                            />
                          </td>
                          <td>
                            <Input
                              type="number"
                              step="any" // or use "0.01" if you want only two decimal places
                              value={item.quantity}
                              placeholder="Quantity"
                              onChange={(e) =>
                                handleInputChange(
                                  index,
                                  "quantity",
                                  e.target.value
                                )
                              }
                            />
                          </td>

                          {state.isTaxRequired && (
                            <>
                              <td>
                                <Input
                                  type="select"
                                  value={item.tax1Type}
                                  onChange={(e) =>
                                    handleInputChange(
                                      index,
                                      "tax1Type",
                                      e.target.value
                                    )
                                  }
                                >
                                  <option value="">Select Tax 1</option>
                                  {TAX_OPTIONS.map((option) => (
                                    <option
                                      key={option.value}
                                      value={option.value}
                                    >
                                      {option.label}
                                    </option>
                                  ))}
                                </Input>
                              </td>
                              <td>
                                <Input
                                  type="select"
                                  value={item.tax2Type}
                                  onChange={(e) =>
                                    handleInputChange(
                                      index,
                                      "tax2Type",
                                      e.target.value
                                    )
                                  }
                                >
                                  <option value="">Select Tax 2</option>
                                  {availableTax2Options.map((option) => (
                                    <option
                                      key={option.value}
                                      value={option.value}
                                    >
                                      {option.label}
                                    </option>
                                  ))}
                                </Input>
                              </td>
                            </>
                          )}
                          <td>
                            <Input
                              type="text"
                              value={item.total}
                              placeholder="Total"
                              readOnly
                            />
                          </td>
                          <td>
                            <Button
                              color="danger"
                              outline
                              size="sm"
                              onClick={() => removeItem(index)}
                              className="d-flex align-items-center justify-content-center w-auto h-auto"
                            >
                              ×
                            </Button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
                <Button
                  className="mt-3"
                  color="success"
                  outline
                  onClick={addNewItem}
                >
                  + Add New
                </Button>
              </div>
            </div>

            <Row>
              <Col md={6}>
                <FormGroup>
                  <Label>Notes</Label>
                  <Input
                    type="textarea"
                    rows="4"
                    value={state.notes}
                    onChange={(e) => updateState("notes", e.target.value)}
                  />
                </FormGroup>
              </Col>
              <Col md={6}>
                <div className="border p-3">
                  <div className="d-flex justify-content-between mb-2">
                    <span>Subtotal:</span>
                    <span className="d-flex justify-content-between gap-2 w-25">
                      <div>{state.toSelectedCustomer.toCustomerCurrency}</div>
                      <div>{state.totals.subTotal.toFixed(2)}</div>
                    </span>
                  </div>
                  {state.isTaxRequired && state.invoiceItems.length > 0 && (
                    <>
                      <div className="d-flex justify-content-between mb-2">
                        {state.invoiceItems[0].tax1Type && (
                          <>
                            <span>
                              Tax 1 ({state.invoiceItems[0].tax1Type}):
                            </span>
                            <span className="d-flex justify-content-between gap-2 w-25">
                              <div>
                                {state.toSelectedCustomer.toCustomerCurrency}
                              </div>
                              <div>{state.totals.tax1Total.toFixed(2)}</div>
                            </span>
                          </>
                        )}
                      </div>
                      <div className="d-flex justify-content-between mb-2">
                        {state.invoiceItems[0].tax2Type && (
                          <>
                            <span>
                              Tax 2 ({state.invoiceItems[0].tax2Type}):
                            </span>
                            <span className="d-flex justify-content-between gap-2 w-25">
                              <div>
                                {state.toSelectedCustomer.toCustomerCurrency}
                              </div>
                              <div>{state.totals.tax2Total.toFixed(2)}</div>
                            </span>
                          </>
                        )}
                      </div>
                    </>
                  )}
                  <div className="d-flex justify-content-between mt-2">
                    <span>Amount Paid:</span>
                    <span
                      className="d-flex justify-content-between gap-2 w-25"
                      style={{ cursor: "pointer" }}
                    >
                      <div>{state.toSelectedCustomer.toCustomerCurrency}</div>
                      <div>
                        <Input
                          type="text"
                          value={state.amountPaid || "0.00"}
                          onChange={handleAmountPaidChange}
                          autoFocus
                          className="form-control form-control-sm text-end"
                        />
                      </div>
                    </span>
                  </div>
                  <div className="d-flex justify-content-between mt-2">
                    <span className="fw-bold">Total Amount:</span>
                    <span className="fw-bold d-flex justify-content-between gap-2 w-25">
                      <div>{state.toSelectedCustomer.toCustomerCurrency}</div>
                      <div>{state.totals.balanceDue.toFixed(2)}</div>
                    </span>
                  </div>
                </div>
              </Col>
            </Row>
          </Form>
        </Card>
      </Container>
      <Footer
        saveText={invoiceId ? "Update" : "Save"}
        save={formik.handleSubmit}
      />
    </div>
  );
};

export default NewInvoice;
